import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import Button from '~/components/isolated/Button/Button';
import {
  VARIANT as BUTTON_VARIANT,
  SIZE,
} from '~/components/isolated/Button/constants';
import { Spinner as LoadingSpinner } from '~/components/isolated/SpinnerWithLoadMore';
import Typography, { VARIANT } from '~/components/isolated/Typography';
import { VERIFICATION } from '~/constants/verification';
import { useGlobal } from '~/hooks/useGlobal';

import { cartStore, userStore } from '~/stores';

import type { SubtotalInfoProps } from '../../types';

import OrderBreakdown from './components/OrderBreakdown/OrderBreakdown';
import OrderInfo from './components/OrderInfo/OrderInfo';
import OrderTotal from './components/OrderTotal/OrderTotal';
import PromoCodeInput from './components/PromoCodeInput/PromoCodeInput';
import styles from './OrderSummary.module.scss';

/**
 * OrderSummary component
 * Displays order summary with promocode input, price breakdown, and checkout button
 */
const OrderSummary = observer(
  ({
    isWarehouseClosed,
    isLoading,
    onCheckout,
    onNavigateToCheckout,
  }: SubtotalInfoProps) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();

    const hasSelectedItems = cartStore.selectedItemsTotalCount > 0;

    const renderCheckoutButton = () => {
      if (!hasSelectedItems) {
        return (
          <div className={styles.buttonWrapper}>
            <Button
              variant={BUTTON_VARIANT.brand}
              size={SIZE.L}
              disabled
              className={styles.checkoutButton}
            >
              {t('goToCheckout')}
            </Button>
            <Typography
              variant={VARIANT['Text 100/Regular High']}
              className={styles.buttonHint}
            >
              {t('selectProductsToContinue')}
            </Typography>
          </div>
        );
      }

      const defaultButton = (
        <div className={styles.buttonWrapper}>
          <Button
            variant={BUTTON_VARIANT.brand}
            size={SIZE.L}
            onClick={onNavigateToCheckout}
            disabled={isLoading || isWarehouseClosed}
            className={styles.checkoutButton}
          >
            {t('checkout')}
          </Button>
        </div>
      );

      if (!company.needVerification) {
        return userStore.isAuthorized ? (
          defaultButton
        ) : (
          <div className={styles.buttonWrapper}>
            <Button
              variant={BUTTON_VARIANT.brand}
              size={SIZE.L}
              onClick={onCheckout}
              className={styles.checkoutButton}
            >
              {t('logInToCheckout')}
            </Button>
            {company.showCartAdditionalTextMobile && (
              <Typography
                variant={VARIANT['Text 100/Medium High']}
                className={styles.buttonHint}
              >
                {t('phrases:verifyIdDescription')}
              </Typography>
            )}
          </div>
        );
      }

      switch (userStore.personalData.kycStatus) {
        case KYCStatus.Verified:
          return defaultButton;

        case KYCStatus.InProgress:
        case KYCStatus.ResubmissionRequested:
        case KYCStatus.Started: {
          const statusConfig = VERIFICATION[userStore.personalData.kycStatus];
          const isDisabled =
            userStore.personalData.kycStatus === KYCStatus.InProgress;

          return (
            <div className={styles.buttonWrapper}>
              <Button
                variant={BUTTON_VARIANT.brand}
                size={SIZE.L}
                onClick={onCheckout}
                disabled={isDisabled}
                className={styles.checkoutButton}
              >
                {statusConfig.title}
              </Button>
              <Typography
                variant={VARIANT['Text 100/Medium High']}
                className={styles.buttonHint}
              >
                {statusConfig.description || ''}
              </Typography>
            </div>
          );
        }

        default:
          return (
            <div className={styles.buttonWrapper}>
              <Button
                variant={BUTTON_VARIANT.brand}
                size={SIZE.L}
                onClick={onCheckout}
                className={styles.checkoutButton}
              >
                {userStore.isAuthorized ? t('verifyId') : t('logInToCheckout')}
              </Button>
            </div>
          );
      }
    };

    return (
      <div className={styles.container}>
        {hasSelectedItems && <PromoCodeInput />}

        {hasSelectedItems && (
          <div className={styles.checkoutInfo}>
            <OrderBreakdown />
            <OrderTotal amount={cartStore.totalCartPrice.paidWithDiscount} />

            <OrderInfo />
          </div>
        )}

        {!isMobile && renderCheckoutButton()}

        {cartStore.calculationProcess.isLoading && (
          <div className={styles.loader}>
            <LoadingSpinner />
          </div>
        )}
      </div>
    );
  },
);

export default OrderSummary;
